import React, { useState, memo, useEffect, useRef } from 'react';
import type { DataGridColumn } from '../DataGrid.types';
import type { DensityType } from './TableHeader';
import { DateCell } from './CellRenderers/DateCell';
import { CurrencyCell } from './CellRenderers/CurrencyCell';
import { NumberCell } from './CellRenderers/NumberCell';
import { PercentCell } from './CellRenderers/PercentCell';
import { PriceChangeCell } from './CellRenderers/PriceChangeCell';

interface CellStyleParams<T> {
  value: unknown;
  row: T;
  column: DataGridColumn<T>;
}

interface TableRowProps<T extends Record<string, unknown>> {
  row: T;
  columns: DataGridColumn<T>[];
  rowIndex?: number;
  onClick?: (row: T) => void;
  className?: string;
  indent?: number;
  isEven?: boolean;
  enableSelection?: boolean;
  isSelected?: boolean;
  onToggleSelect?: (row: T) => void;
  // Column reorder
  draggedColumnId?: string | null;
  getColumnStyle?: (columnId: string) => React.CSSProperties;
  // Density
  density?: DensityType;
  // Cell flash animation
  enableCellFlash?: boolean;
  previousRowData?: T | null;
  // Conditional styling
  cellStyle?: (params: CellStyleParams<T>) => React.CSSProperties | undefined;
}

// Density-based row heights and padding
const DENSITY_CONFIG = {
  compact: { rowHeight: '32px', padding: { vertical: '4px', horizontal: '10px' } },
  normal: { rowHeight: '40px', padding: { vertical: '8px', horizontal: '14px' } },
  comfortable: { rowHeight: '48px', padding: { vertical: '12px', horizontal: '16px' } }
};

// Checkbox component
const Checkbox = ({ checked, indeterminate, onChange }: {
  checked: boolean;
  indeterminate?: boolean;
  onChange: () => void;
}) => (
  <label
    style={{
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      cursor: 'pointer'
    }}
    onClick={(e) => e.stopPropagation()}
  >
    <input
      type="checkbox"
      checked={checked}
      ref={(el) => {
        if (el) el.indeterminate = indeterminate || false;
      }}
      onChange={onChange}
      style={{ display: 'none' }}
    />
    <span
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: '16px',
        height: '16px',
        borderRadius: '4px',
        border: checked || indeterminate
          ? '2px solid var(--color-primary)'
          : '2px solid var(--color-neutral-300)',
        background: checked || indeterminate
          ? 'var(--color-primary)'
          : 'transparent',
        transition: 'all var(--transition-fast)'
      }}
    >
      {checked && (
        <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
          <path
            d="M1.5 5l2.5 2.5 4.5-4.5"
            stroke="white"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      )}
      {indeterminate && !checked && (
        <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
          <path
            d="M2 5h6"
            stroke="white"
            strokeWidth="1.5"
            strokeLinecap="round"
          />
        </svg>
      )}
    </span>
  </label>
);

// Flash cell component for value changes
interface FlashCellProps {
  children: React.ReactNode;
  previousValue: unknown;
  currentValue: unknown;
  enableFlash: boolean;
}

const FlashCell = memo(({ children, previousValue, currentValue, enableFlash }: FlashCellProps) => {
  const [flashClass, setFlashClass] = useState<'positive' | 'negative' | null>(null);
  const hasFlashed = useRef(false);

  useEffect(() => {
    if (!enableFlash || previousValue === undefined || previousValue === null) {
      return;
    }

    // Only flash if values are different and both are numbers
    if (typeof previousValue === 'number' && typeof currentValue === 'number') {
      if (previousValue !== currentValue && !hasFlashed.current) {
        hasFlashed.current = true;
        setFlashClass(currentValue > previousValue ? 'positive' : 'negative');

        const timer = setTimeout(() => {
          setFlashClass(null);
          hasFlashed.current = false;
        }, 800);

        return () => clearTimeout(timer);
      }
    }
  }, [currentValue, previousValue, enableFlash]);

  return (
    <span
      className={flashClass === 'positive' ? 'animate-flash-positive' : flashClass === 'negative' ? 'animate-flash-negative' : ''}
      style={{
        display: 'block',
        borderRadius: '2px',
        transition: 'background-color 0.3s ease'
      }}
    >
      {children}
    </span>
  );
});

FlashCell.displayName = 'FlashCell';

// Render cell based on cellRenderer type
function renderCellWithRenderer<T extends Record<string, unknown>>(
  value: unknown,
  column: DataGridColumn<T>,
  density: DensityType = 'normal'
): React.ReactNode {
  const cellParams = column.cellParams || {};

  // Use appropriate cell renderer based on column.cellRenderer
  switch (column.cellRenderer) {
    case 'date':
      return <DateCell value={value as Date | string | number | null | undefined} format={cellParams.format || 'short'} />;

    case 'currency':
      return (
        <CurrencyCell
          value={value as number | null | undefined}
          currency={cellParams.currency || 'TRY'}
          decimals={cellParams.decimals ?? 2}
          colorCoded={cellParams.colorCoded}
        />
      );

    case 'number':
      return (
        <NumberCell
          value={value as number | null | undefined}
          decimals={cellParams.decimals ?? 2}
          colorCoded={cellParams.colorCoded}
        />
      );

    case 'percent':
      return (
        <PercentCell
          value={value as number | null | undefined}
          decimals={cellParams.decimals ?? 2}
          colorCoded={cellParams.colorCoded}
        />
      );

    case 'priceChange':
      return <PriceChangeCell value={value as number | null | undefined} />;

    default:
      return formatCellValue(value, density);
  }
}

function TableRowComponent<T extends Record<string, unknown>>({
  row,
  columns,
  rowIndex = 0,
  onClick,
  className = '',
  indent = 0,
  isEven = false,
  enableSelection = false,
  isSelected = false,
  onToggleSelect,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  draggedColumnId: _draggedColumnId,
  getColumnStyle,
  density = 'normal',
  enableCellFlash = false,
  previousRowData,
  cellStyle
}: TableRowProps<T>): React.ReactElement {
  const [isHovered, setIsHovered] = useState(false);
  const config = DENSITY_CONFIG[density];

  const handleClick = () => {
    onClick?.(row);
  };

  // Zebra striping with subtle opacity difference
  const zebraBackground = isEven
    ? 'var(--color-bg-secondary)'
    : 'var(--color-bg-card)';

  return (
    <tr
      className={className}
      onClick={handleClick}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      style={{
        height: config.rowHeight,
        background: isSelected
          ? 'var(--color-bg-selected)'
          : isHovered
            ? 'var(--color-bg-hover)'
            : zebraBackground,
        cursor: onClick ? 'pointer' : 'default',
        transition: 'background var(--transition-fast)',
        borderLeft: isSelected
          ? '3px solid var(--color-primary)'
          : isHovered
            ? '3px solid var(--color-primary-300)'
            : '3px solid transparent'
      }}
    >
      {/* Selection Checkbox */}
      {enableSelection && (
        <td
          style={{
            padding: `${config.padding.vertical} ${config.padding.horizontal}`,
            width: '40px',
            minWidth: '40px',
            borderBottom: '1px solid var(--color-border-light)',
            borderRight: '1px solid var(--color-border-light)',
            verticalAlign: 'middle'
          }}
        >
          <Checkbox
            checked={isSelected}
            onChange={() => onToggleSelect?.(row)}
          />
        </td>
      )}
      {columns.map((column, index) => {
        const accessorKey = column.accessorKey;
        const accessorFn = column.accessorFn;

        let cellValue: unknown;
        if (accessorFn) {
          cellValue = accessorFn(row);
        } else if (accessorKey) {
          cellValue = row[accessorKey];
        }

        // Get previous cell value for flash animation
        let previousCellValue: unknown;
        if (previousRowData && enableCellFlash) {
          if (accessorFn) {
            previousCellValue = accessorFn(previousRowData);
          } else if (accessorKey) {
            previousCellValue = previousRowData[accessorKey];
          }
        }

        const paddingLeft = index === 0 && indent > 0
          ? `${indent * 20 + parseInt(config.padding.horizontal)}px`
          : config.padding.horizontal;
        const columnDragStyle = getColumnStyle?.(column.id) || {};

        // Get custom cell style
        const customStyle = cellStyle?.({ value: cellValue, row, column });

        // Determine if column should be right-aligned (numeric types)
        const isNumericColumn = column.cellRenderer === 'currency'
          || column.cellRenderer === 'number'
          || column.cellRenderer === 'percent'
          || column.cellRenderer === 'priceChange'
          || (typeof cellValue === 'number' && !column.cell);

        return (
          <td
            key={column.id}
            style={{
              padding: `${config.padding.vertical} ${config.padding.horizontal}`,
              paddingLeft,
              fontSize: density === 'compact' ? '12px' : 'var(--text-sm)',
              color: 'var(--color-text-primary)',
              borderBottom: '1px solid var(--color-border-light)',
              borderRight: '1px solid var(--color-border-light)',
              width: column.width,
              minWidth: column.minWidth,
              maxWidth: column.maxWidth,
              verticalAlign: 'middle',
              position: 'relative',
              textAlign: isNumericColumn ? 'right' : 'left',
              background: isSelected
                ? 'var(--color-bg-selected)'
                : isHovered
                  ? 'var(--color-bg-hover)'
                  : zebraBackground,
              ...columnDragStyle,
              ...customStyle
            }}
          >
            {enableCellFlash && typeof cellValue === 'number' ? (
              <FlashCell
                previousValue={previousCellValue}
                currentValue={cellValue}
                enableFlash={enableCellFlash}
              >
                {column.cell
                  ? column.cell({
                      getValue: () => cellValue,
                      row: { original: row, id: String(rowIndex), index: rowIndex } as Parameters<NonNullable<typeof column.cell>>[0]['row']
                    })
                  : column.cellRenderer
                    ? renderCellWithRenderer(cellValue, column, density)
                    : formatCellValue(cellValue, density)}
              </FlashCell>
            ) : column.cell ? (
              column.cell({
                getValue: () => cellValue,
                row: { original: row, id: String(rowIndex), index: rowIndex } as Parameters<NonNullable<typeof column.cell>>[0]['row']
              })
            ) : column.cellRenderer ? (
              renderCellWithRenderer(cellValue, column, density)
            ) : (
              formatCellValue(cellValue, density)
            )}
          </td>
        );
      })}
    </tr>
  );
}

function formatCellValue(value: unknown, density: DensityType = 'normal'): React.ReactNode {
  if (value === null || value === undefined) {
    return (
      <span style={{ color: 'var(--color-text-muted)' }}>—</span>
    );
  }

  if (typeof value === 'number') {
    return (
      <span
        style={{
          fontFamily: 'var(--font-mono)',
          fontVariantNumeric: 'tabular-nums',
          fontSize: density === 'compact' ? '11px' : '13px'
        }}
      >
        {value.toLocaleString('tr-TR')}
      </span>
    );
  }

  if (typeof value === 'boolean') {
    return (
      <span
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '4px',
          padding: density === 'compact' ? '2px 6px' : '3px 8px',
          borderRadius: 'var(--radius-full)',
          fontSize: density === 'compact' ? '10px' : '11px',
          fontWeight: 500,
          background: value ? 'var(--color-positive-bg)' : 'var(--color-neutral-100)',
          color: value ? 'var(--color-positive)' : 'var(--color-text-secondary)',
          border: value ? '1px solid var(--color-positive-border)' : '1px solid var(--color-border)'
        }}
      >
        {value ? 'Evet' : 'Hayır'}
      </span>
    );
  }

  if (value instanceof Date) {
    const day = String(value.getDate()).padStart(2, '0');
    const month = String(value.getMonth() + 1).padStart(2, '0');
    const year = value.getFullYear();
    return (
      <span style={{ fontFamily: 'var(--font-mono)', fontSize: density === 'compact' ? '11px' : '12px' }}>
        {`${day}.${month}.${year}`}
      </span>
    );
  }

  return String(value);
}

// Memoized export for performance
export const TableRow = memo(TableRowComponent) as typeof TableRowComponent;

// Export Checkbox for reuse
export { Checkbox };

// Export types
export type { CellStyleParams };
